import { useCallback, useSyncExternalStore } from 'react';
import { persistFtueState, type FtueOutcome } from '@/lib/ftue/api';
import { useAppAuth } from '@/lib/useAppAuth';

/**
 * One-shot FTUE completion flag, **keyed per user**. The FTUE plays once,
 * right after signup and before the real lobby; the moment it resolves this
 * flips and the gate never shows the FTUE again — for THAT user. A second
 * account logging in on the same device gets its own flag (and therefore its
 * own FTUE), which is exactly the point of per-user keying.
 *
 * Two layers, one seam:
 * - **Backend is the record**: {@link useFtueCompletion}.markCompleted fires
 *   `PUT /users/me/ftue-state` (first-write-wins server-side) with the
 *   outcome + variant, so the state survives devices and localStorage wipes.
 * - **localStorage is the synchronous per-user cache**: the gate must decide
 *   before any network round trip; the profile read re-seeds it on a fresh
 *   device via `syncFromServer`.
 *
 * All resolution goes through the authed user id (one source, hook-bound).
 * When no user resolves (should not happen post-onboarding) reads and writes
 * fall back to a device-scoped legacy key so they at least stay consistent
 * with each other.
 */
const STORAGE_PREFIX = 'predictions:ftue_completed:';
const LEGACY_DEVICE_KEY = 'predictions:ftue_completed';

const memory = new Map<string, boolean>();
const listeners = new Set<() => void>();

function storageKeyFor(userId: string | null): string {
  return userId == null ? LEGACY_DEVICE_KEY : `${STORAGE_PREFIX}${userId}`;
}

function readFor(userId: string | null): boolean {
  const key = storageKeyFor(userId);
  const cached = memory.get(key);
  if (cached !== undefined) return cached;
  let value = false;
  try {
    value = window.localStorage.getItem(key) === '1';
  } catch {
    // storage unavailable — treat as not completed
  }
  memory.set(key, value);
  return value;
}

function flipFor(userId: string | null): void {
  const key = storageKeyFor(userId);
  memory.set(key, true);
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // storage unavailable — the in-memory cache still holds for this session
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test/spec accessor — read the flag for an explicit user. */
export function isFtueCompletedFor(userId: string | null): boolean {
  return readFor(userId);
}

/**
 * The FTUE completion surface, bound to the authed user:
 * - `completed` — reactive per-user read (re-evaluates on user switch).
 * - `markCompleted` — flip the user's flag (the gate reacts instantly) and
 *   persist outcome + served variant to the backend, fire-and-forget (the
 *   server's first-write-wins makes a lost write safe to repeat next session).
 * - `syncFromServer` — the profile says this user already resolved the FTUE
 *   elsewhere: cache it locally, persist nothing.
 */
export function useFtueCompletion(): {
  completed: boolean;
  markCompleted: (persist: { outcome: FtueOutcome; variant: string }) => void;
  syncFromServer: () => void;
} {
  const { user } = useAppAuth();
  const userId = user?.userId == null ? null : String(user.userId);

  const getSnapshot = useCallback(() => readFor(userId), [userId]);
  const completed = useSyncExternalStore(subscribe, getSnapshot);

  const markCompleted = useCallback(
    (persist: { outcome: FtueOutcome; variant: string }) => {
      flipFor(userId);
      void persistFtueState(persist.outcome, persist.variant).catch((err) => {
        console.warn('[ftue] failed to persist ftue-state (will not retry this session)', err);
      });
    },
    [userId],
  );

  const syncFromServer = useCallback(() => {
    if (!readFor(userId)) flipFor(userId);
  }, [userId]);

  return { completed, markCompleted, syncFromServer };
}

/** Test-only escape hatch. */
export function __resetFtueCompletionForTests(): void {
  memory.clear();
  try {
    window.localStorage.removeItem(LEGACY_DEVICE_KEY);
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) window.localStorage.removeItem(key);
    }
  } catch {
    // storage unavailable
  }
}
